import { buildHairMotionSection, subscribeFt3dHairMotion } from '../shared/ft3d-hair-motion';
// ft3d-hud/hud-prefs.ts — viewer-local controls rendered into the Settings window's Log and
// Display tabs.
//
// The dock buttons stay the fast path; this is the discoverable one. Both drive the SAME state
// through log.ts / dice-window.ts rather than keeping a second copy, and both re-render from the
// subscription, so changing the mode in either place updates the other.
//
// Mount points are provided by my_campaign.php's Settings window. Absent on a route that has no
// Settings window, this is simply a no-op.

import type { FtLogMode } from './types';
import { readFt3dFireMotion, writeFt3dFireMotion, subscribeFt3dFireMotion, type Ft3dFireMotion } from '../shared/ft3d-fire-motion';
import { LOG_MODES, LOG_MODE_LABEL, getLogMode, setLogMode, subscribeLogMode } from './log';
import { isDiceWindowOpen, setDiceWindowOpen, subscribeDiceWindow } from './dice-window';
import {
    getFt3dFloatingDamageSize,
    getFt3dFloatingDamageMotion,
    isFt3dFloatingDamageEnabled,
    setFt3dFloatingDamageEnabled,
    setFt3dFloatingDamageSize,
    setFt3dFloatingDamageMotion,
    type Ft3dFloatingDamageSize,
    type Ft3dFloatingDamageMotion,
} from './damage-feedback';
import {
    FT3D_GRAPHICS_QUALITY_DEFINITIONS,
    FT3D_GRAPHICS_QUALITY_QUERY_KEY,
    readFt3dGraphicsQuality,
    writeFt3dGraphicsQuality,
    type Ft3dGraphicsQuality,
} from '../shared/ft3d-graphics-quality';

const MODE_HINT: Record<FtLogMode, string> = {
    off: 'No roll lines are shown at all.',
    fade: 'Lines rise from the bottom and fade out on their own.',
    on: 'Lines stack up and the list scrolls.',
};

function buildLogSection(): { root: HTMLElement; sync: (mode: FtLogMode) => void } {
    const section = document.createElement('section');
    section.className = 'ft3d-hud-prefs__group';
    section.innerHTML = '<h3>Combat log</h3>';

    const choices = document.createElement('div');
    choices.className = 'ft3d-hud-prefs__choices';
    section.appendChild(choices);

    const hint = document.createElement('p');
    hint.className = 'ft3d-hud-prefs__hint';
    section.appendChild(hint);

    const buttons = LOG_MODES.map((mode) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ft3d-hud-prefs__choice';
        button.textContent = LOG_MODE_LABEL[mode];
        button.setAttribute('data-ft3d-log-mode-choice', mode);
        button.setAttribute('aria-pressed', 'false');
        button.addEventListener('click', () => setLogMode(mode));
        choices.appendChild(button);
        return { mode, button };
    });

    const sync = (current: FtLogMode): void => {
        buttons.forEach(({ mode, button }) => {
            const active = mode === current;
            button.setAttribute('aria-pressed', active ? 'true' : 'false');
            button.classList.toggle('is-active', active);
        });
        hint.textContent = MODE_HINT[current];
    };
    return { root: section, sync };
}

function buildDiceSection(): { root: HTMLElement; sync: (open: boolean) => void } {
    const section = document.createElement('section');
    section.className = 'ft3d-hud-prefs__group';
    section.innerHTML = '<h3>Dice window</h3>';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ft3d-hud-prefs__choice';
    button.setAttribute('data-ft3d-dice-window-choice', '');
    button.addEventListener('click', () => setDiceWindowOpen(!isDiceWindowOpen()));
    section.appendChild(button);

    const hint = document.createElement('p');
    hint.className = 'ft3d-hud-prefs__hint';
    hint.textContent = 'The quick roller, the complex roller and the ADV/DIS stacks.';
    section.appendChild(hint);

    const sync = (open: boolean): void => {
        button.textContent = open ? 'Hide dice window' : 'Show dice window';
        button.setAttribute('aria-pressed', open ? 'true' : 'false');
        button.classList.toggle('is-active', open);
    };
    return { root: section, sync };
}

const POINTER_COORDS_KEY = 'ft3d.hud.pointerCoords';
const FRAME_INFORMATION_KEY = 'ft3d.hud.frameInformation';

function readLocalToggle(key: string): boolean {
    try { return window.localStorage.getItem(key) === 'on'; } catch { return false; }
}

function writeLocalToggle(key: string, show: boolean): void {
    try { window.localStorage.setItem(key, show ? 'on' : 'off'); } catch { /* viewer-local nicety */ }
}

let syncFrameInformationPreference: ((show: boolean) => void) | null = null;

function setFrameInformationPreference(show: boolean): void {
    writeLocalToggle(FRAME_INFORMATION_KEY, show);
    syncFrameInformationPreference?.(show);
}

/**
 * The scene's pointer read-out (X · Y · Z). Developer detail rather than table information, so it is
 * off by default and lives here instead of as a permanent strip on the map. The read-out itself is
 * inside the scene iframe; the route forwards this choice to it via window.ftSetFt3dPointerCoords.
 */
function buildPointerCoordsSection(): HTMLElement {
    const section = document.createElement('section');
    section.className = 'ft3d-hud-prefs__group';
    section.innerHTML = '<h3>Scene</h3>';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ft3d-hud-prefs__choice';
    button.setAttribute('data-ft3d-pointer-coords-choice', '');
    section.appendChild(button);

    const hint = document.createElement('p');
    hint.className = 'ft3d-hud-prefs__hint';
    hint.textContent = 'Show the pointer’s X · Y · Z position on the map.';
    section.appendChild(hint);

    const sync = (show: boolean): void => {
        button.textContent = show ? 'Hide pointer position' : 'Show pointer position';
        button.setAttribute('aria-pressed', show ? 'true' : 'false');
        button.classList.toggle('is-active', show);
        const apply = (window as unknown as { ftSetFt3dPointerCoords?: (visible: boolean) => void })
            .ftSetFt3dPointerCoords;
        if (typeof apply === 'function') apply(show);
    };
    button.addEventListener('click', () => {
        const next = !readLocalToggle(POINTER_COORDS_KEY);
        writeLocalToggle(POINTER_COORDS_KEY, next);
        sync(next);
    });
    sync(readLocalToggle(POINTER_COORDS_KEY));
    return section;
}

/** The existing top-left renderer panel: FPS, frame time, draw calls and scene resource counts. */
function buildFrameInformationSection(): HTMLElement {
    const section = document.createElement('section');
    section.className = 'ft3d-hud-prefs__group';
    section.innerHTML = '<h3>Performance</h3>';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ft3d-hud-prefs__choice';
    button.setAttribute('data-ft3d-frame-information-choice', '');
    section.appendChild(button);

    const hint = document.createElement('p');
    hint.className = 'ft3d-hud-prefs__hint';
    hint.textContent = 'Open a draggable window with live FPS, frame time, draw calls, geometry and texture counts.';
    section.appendChild(hint);

    const sync = (show: boolean): void => {
        button.textContent = show ? 'Hide performance stats' : 'Show performance stats';
        button.setAttribute('aria-pressed', show ? 'true' : 'false');
        button.classList.toggle('is-active', show);
        const apply = (window as unknown as { ftSetFt3dFrameInformation?: (visible: boolean) => void })
            .ftSetFt3dFrameInformation;
        if (typeof apply === 'function') apply(show);
    };
    syncFrameInformationPreference = sync;
    button.addEventListener('click', () => {
        const next = !readLocalToggle(FRAME_INFORMATION_KEY);
        setFrameInformationPreference(next);
    });
    sync(readLocalToggle(FRAME_INFORMATION_KEY));
    return section;
}

/**
 * Reload only the scene iframe after the choice is stored. The parent Settings window remains
 * usable while the iframe's own loader covers its canvas, preserving the during-load input rule.
 */
function reloadSceneForGraphicsQuality(quality: Ft3dGraphicsQuality): boolean {
    const frame = document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
    const source = frame?.getAttribute('src') || frame?.getAttribute('data-src');
    if (!frame || !source) return false;
    const nextUrl = new URL(source, window.location.href);
    nextUrl.searchParams.set(FT3D_GRAPHICS_QUALITY_QUERY_KEY, quality);
    const nextSource = nextUrl.toString();
    frame.setAttribute('data-src', nextSource);
    frame.setAttribute('src', nextSource);
    return true;
}

function buildFireMotionSection(): { root: HTMLElement; sync: (value: Ft3dFireMotion) => void } {
    const root = document.createElement('section');
    root.className = 'ft3d-hud-prefs__group';
    root.innerHTML = '<h3>Fire animation</h3>';
    const choices = document.createElement('div');
    choices.className = 'ft3d-hud-prefs__choices';
    choices.setAttribute('aria-label', 'Fire animation');
    root.appendChild(choices);
    const buttons = (['animated', 'system', 'paused'] as const).map(value => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ft3d-hud-prefs__choice';
        button.dataset.ft3dFireMotionChoice = value;
        button.textContent = { animated: 'Animate', system: 'Follow system', paused: 'Pause' }[value];
        button.addEventListener('click', () => writeFt3dFireMotion(value));
        choices.appendChild(button);
        return { value, button };
    });
    const hint = document.createElement('p');
    hint.className = 'ft3d-hud-prefs__hint';
    root.appendChild(hint);
    const sync = (value: Ft3dFireMotion): void => {
        for (const entry of buttons) {
            entry.button.setAttribute('aria-pressed', String(entry.value === value));
            entry.button.classList.toggle('is-active', entry.value === value);
        }
        hint.textContent = value === 'animated'
            ? 'Torches, braziers, candles and other flames animate, even with system reduced motion enabled.'
            : value === 'system' ? 'Flames pause when your system requests reduced motion.'
                : 'Flames remain lit but their animation is paused.';
    };
    sync(readFt3dFireMotion());
    return { root, sync };
}

function buildGraphicsQualitySection(): HTMLElement {
    const section = document.createElement('section');
    section.className = 'ft3d-hud-prefs__group';
    section.innerHTML = '<h3>Graphics quality</h3>';

    const choices = document.createElement('div');
    choices.className = 'ft3d-hud-prefs__choices';
    choices.setAttribute('role', 'group');
    choices.setAttribute('aria-label', 'Terrain graphics quality');
    section.appendChild(choices);

    const hint = document.createElement('p');
    hint.className = 'ft3d-hud-prefs__hint';
    section.appendChild(hint);

    const status = document.createElement('p');
    status.className = 'ft3d-hud-prefs__hint';
    status.setAttribute('data-ft3d-graphics-quality-status', '');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    section.appendChild(status);

    let current = readFt3dGraphicsQuality();
    const buttons = FT3D_GRAPHICS_QUALITY_DEFINITIONS.map((definition) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ft3d-hud-prefs__choice';
        button.textContent = definition.label;
        button.setAttribute('data-ft3d-graphics-quality-choice', definition.value);
        button.setAttribute('aria-pressed', 'false');
        choices.appendChild(button);
        return { definition, button };
    });
    const sync = (): void => {
        buttons.forEach(({ definition, button }) => {
            const active = definition.value === current;
            button.setAttribute('aria-pressed', active ? 'true' : 'false');
            button.classList.toggle('is-active', active);
        });
        const definition = FT3D_GRAPHICS_QUALITY_DEFINITIONS.find((candidate) => candidate.value === current)!;
        hint.textContent = definition.summary;
    };
    buttons.forEach(({ definition, button }) => {
        button.addEventListener('click', () => {
            const changed = current !== definition.value;
            current = definition.value;
            writeFt3dGraphicsQuality(current);
            sync();
            if (!changed) {
                status.textContent = `${definition.label} is already active.`;
                return;
            }
            status.textContent = reloadSceneForGraphicsQuality(current)
                ? `Reloading the scene with ${definition.label} terrain…`
                : `${definition.label} saved for the next 3D scene.`;
        });
    });
    sync();
    return section;
}

/** Viewer-local combat presentation; the authoritative log remains available when this is off. */
function buildFloatingDamageSection(): HTMLElement {
    const section = document.createElement('section');
    section.className = 'ft3d-hud-prefs__group';
    section.innerHTML = '<h3>Combat feedback</h3><h4 class="h6">Overhead combat text</h4>';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ft3d-hud-prefs__choice';
    button.setAttribute('data-ft3d-floating-damage-choice', '');
    section.appendChild(button);

    const hint = document.createElement('p');
    hint.className = 'ft3d-hud-prefs__hint';
    hint.textContent = 'Show damage, healing, vitality, mana and luck rolls, misses, glancing blows, critical hits, and triggered ability or spell names above perceived creatures.';
    section.appendChild(hint);

    const sizeHeading = document.createElement('h4');
    sizeHeading.className = 'h6 mt-3';
    sizeHeading.textContent = 'Font size';
    section.appendChild(sizeHeading);

    const sizeChoices = document.createElement('div');
    sizeChoices.className = 'ft3d-hud-prefs__choices';
    section.appendChild(sizeChoices);
    const sizes: Array<{ value: Ft3dFloatingDamageSize; label: string }> = [
        { value: 'small', label: 'Small' },
        { value: 'normal', label: 'Normal' },
        { value: 'large', label: 'Large' },
    ];
    const sizeButtons = sizes.map(({ value, label }) => {
        const choice = document.createElement('button');
        choice.type = 'button';
        choice.className = 'ft3d-hud-prefs__choice';
        choice.textContent = label;
        choice.setAttribute('data-ft3d-floating-damage-size-choice', value);
        choice.setAttribute('aria-pressed', 'false');
        choice.addEventListener('click', () => {
            setFt3dFloatingDamageSize(value);
            syncSize(value);
        });
        sizeChoices.appendChild(choice);
        return { value, choice };
    });

    const motionHeading = document.createElement('h4');
    motionHeading.className = 'h6 mt-3';
    motionHeading.textContent = 'Motion';
    section.appendChild(motionHeading);

    const motionChoices = document.createElement('div');
    motionChoices.className = 'ft3d-hud-prefs__choices';
    section.appendChild(motionChoices);
    const motions: Array<{ value: Ft3dFloatingDamageMotion; label: string }> = [
        { value: 'always', label: 'Animate' },
        { value: 'system', label: 'Follow system' },
    ];
    const motionButtons = motions.map(({ value, label }) => {
        const choice = document.createElement('button');
        choice.type = 'button';
        choice.className = 'ft3d-hud-prefs__choice';
        choice.textContent = label;
        choice.setAttribute('data-ft3d-floating-damage-motion-choice', value);
        choice.setAttribute('aria-pressed', 'false');
        choice.addEventListener('click', () => {
            setFt3dFloatingDamageMotion(value);
            syncMotion(value);
        });
        motionChoices.appendChild(choice);
        return { value, choice };
    });
    const osReducesMotion = typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const motionHint = document.createElement('p');
    motionHint.className = 'ft3d-hud-prefs__hint';
    motionHint.textContent = osReducesMotion
        ? 'Your system asks for reduced motion. "Follow system" shows the numbers already spread out with a plain fade; "Animate" keeps the waterfall.'
        : '"Follow system" drops the waterfall to a plain fade whenever your system asks for reduced motion.';
    section.appendChild(motionHint);

    const sync = (enabled: boolean): void => {
        button.textContent = enabled ? 'On — click to turn off' : 'Off — click to turn on';
        button.setAttribute('aria-pressed', enabled ? 'true' : 'false');
        button.classList.toggle('is-active', enabled);
    };
    const syncSize = (size: Ft3dFloatingDamageSize): void => {
        sizeButtons.forEach(({ value, choice }) => {
            const active = value === size;
            choice.setAttribute('aria-pressed', active ? 'true' : 'false');
            choice.classList.toggle('is-active', active);
        });
    };
    const syncMotion = (motion: Ft3dFloatingDamageMotion): void => {
        motionButtons.forEach(({ value, choice }) => {
            const active = value === motion;
            choice.setAttribute('aria-pressed', active ? 'true' : 'false');
            choice.classList.toggle('is-active', active);
        });
    };
    button.addEventListener('click', () => {
        const next = !isFt3dFloatingDamageEnabled();
        setFt3dFloatingDamageEnabled(next);
        sync(next);
    });
    sync(isFt3dFloatingDamageEnabled());
    syncSize(getFt3dFloatingDamageSize());
    syncMotion(getFt3dFloatingDamageMotion());
    return section;
}

/** Drops the previous render's subscriptions, so a remount cannot leave orphans in the sets. */
let teardown: Array<() => void> = [];

export function mountFt3dHudPrefs(): void {
    const mount = document.querySelector<HTMLElement>('[data-ft3d-hud-prefs-mount]');
    const displayMount = document.querySelector<HTMLElement>('[data-ft3d-display-prefs-mount]');
    if (!mount && !displayMount) return;
    teardown.forEach((off) => off());
    teardown = [];
    if (mount) mount.innerHTML = '';
    if (displayMount) displayMount.innerHTML = '';

    if (mount) {
        const log = buildLogSection();
        const dice = buildDiceSection();
        mount.appendChild(log.root);
        mount.appendChild(dice.root);
        teardown.push(subscribeLogMode(log.sync));
        teardown.push(subscribeDiceWindow(dice.sync));
        // Belt: the first sync fires before the dock has applied its stored value on some paths.
        log.sync(getLogMode());
    }

    if (displayMount) {
        const graphicsQuality = buildGraphicsQualitySection();
        displayMount.appendChild(graphicsQuality);
        const hairMotion = buildHairMotionSection();
        displayMount.appendChild(hairMotion.root);
        teardown.push(subscribeFt3dHairMotion(hairMotion.sync));
        const fireMotion = buildFireMotionSection();
        displayMount.appendChild(fireMotion.root);
        teardown.push(subscribeFt3dFireMotion(fireMotion.sync));
        const floatingDamage = buildFloatingDamageSection();
        displayMount.appendChild(floatingDamage);
        const frameInformation = buildFrameInformationSection();
        displayMount.appendChild(frameInformation);
        const pointerCoords = buildPointerCoordsSection();
        displayMount.appendChild(pointerCoords);
        (window as unknown as {
            ftSetFt3dFrameInformationPreference?: (show: boolean) => void;
        }).ftSetFt3dFrameInformationPreference = setFrameInformationPreference;
    }
}
